import React from 'react';
import { Tooltip } from 'antd';

// EAA-1667: AuthProps/useAuthDecision moved to packages/hooks; withAuthGuard is the
// only auth piece that stays in packages/ui (it renders antd Tooltip + reads this
// package's i18n), so it consumes the decision engine from @ai-content-hub/hooks.
import type { AuthFallback, AuthProps } from '@ai-content-hub/hooks';
import { useAuthDecision } from '@ai-content-hub/hooks';
import { useUIText } from '../i18n';

export interface WithAuthGuardOptions {
    /** Fallback used when the caller doesn't pass `authFallback`. */
    defaultFallback?: AuthFallback;
    /** Which prop to force truthy for the `disable`/`tooltip` fallbacks. */
    disabledPropName?: string;
    /** Overrides the shipped `common.noPermissionTooltip` catalog string. */
    tooltipTitle?: string;
}

/**
 * Applies the `auth`/`authFallback` contract to `Component` once, at export
 * time — inheritance by construction: every consumer gets gating behavior
 * automatically, whether or not it passes `auth`. No-op (renders `Component`
 * unchanged) when `auth` isn't passed.
 */
export function withAuthGuard<P extends object>(
    Component: React.ComponentType<P>,
    options: WithAuthGuardOptions = {}
) {
    const { defaultFallback = 'disable', disabledPropName = 'disabled', tooltipTitle } = options;

    const Guarded = React.forwardRef<unknown, P & AuthProps>((props, ref) => {
        const { auth, authFallback = defaultFallback, ...rest } = props;
        const decision = useAuthDecision(auth, authFallback);
        const { common } = useUIText();

        if (decision === 'allow') {
            return <Component ref={ref} {...(rest as P)} />;
        }

        if (decision === 'hide') {
            return null;
        }

        const disabledProps = { [disabledPropName]: true } as unknown as P;

        if (decision === 'tooltip') {
            return (
                <Tooltip title={tooltipTitle ?? common.noPermissionTooltip}>
                    <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                        <Component ref={ref} {...(rest as P)} {...disabledProps} />
                    </span>
                </Tooltip>
            );
        }

        return <Component ref={ref} {...(rest as P)} {...disabledProps} />;
    });

    const name = Component.displayName || Component.name || 'Component';
    Guarded.displayName = `withAuthGuard(${name})`;

    return Guarded as unknown as React.ForwardRefExoticComponent<
        P & AuthProps & React.RefAttributes<unknown>
    >;
}
